import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { filter, tap } from 'rxjs';
import { MenuTool } from '../../../interfaces/MenuTool.interface';
import { NavigationItem } from '../../../interfaces/navigationList.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../../../interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from '../../../services/page-container.service';
import { ProfileModalService } from '../../../../../../shared/services/profileModal.service';
import { SharedService } from '../../../../../../shared/services/shared.service';
import { AuthenticationService } from '../../../../../auth/services/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'st-mobile-main-sections-left-menu',
  standalone: true,
  imports: [CommonModule, MenuModule],
  templateUrl: './mobile-main-sections-left-menu.component.html',
  styleUrls: ['./mobile-main-sections-left-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMainSectionsLeftMenuComponent {
  navigationList = input.required<NavigationItem[]>();
  homeButton = input.required<HomeButtonConfig>();
  profile = input.required<Profile>();
  settingURL = input<string>();
  tools = input.required<MenuTool[]>();
  public isAboutModalVisible = signal<boolean>(false);
  dropDownItems: MenuItem[] = [];
  public userName = signal<string>('');
  public userEmail = signal<string>('');

  constructor(
    readonly cd: ChangeDetectorRef,
    readonly pageContainerService: PageContainerService,
    readonly router: Router,
    private readonly _renderer: Renderer2,
    private readonly profileModalService: ProfileModalService,
    private readonly _sharedService: SharedService,
    private readonly _router: Router,
    public authenticationService: AuthenticationService,
  ) {
    this.setUserDetailsFromLocalStorage();
  }

  private readonly destroyRef = inject(DestroyRef);

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      this.userName.set(user?.user_profile?.full_name ?? 'Unknown User');
      this.userEmail.set(user?.user_profile?.email ?? 'Unknown Email');

      this.dropDownItems = [
        {
          label: this.userName(),
          items: [
            {
              label: 'My Profile',
              icon: 'icon-user-circle',
              shortcut: '⌘+E',
              disabled: false,
              command: () => this.profileModalService.openProfileModal(),
            },
            {
              label: 'About Adroit',
              icon: 'icon-info',
              shortcut: '⌘+a',
              disabled: false,
              command: () => this.profileModalService.openAboutModal(),
            },
            {
              label: 'Sign Out',
              icon: 'icon-sign-out',
              shortcut: '⌘+d',
              disabled: false,
              command: () => this.signOut(),
            },
          ],
        },
      ];
    }
  }

  onSelectSection = output<NavigationItem>();
  onToolsOpen = output<void>();

  isStaticButtonActive = signal<'home' | 'profile' | 'sitting' | ''>('');

  routerEvents$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    tap(event => {
      const url = event.urlAfterRedirects;
      this.checkStaticButtons(url);
      this.pageContainerService.expandMenuDrawer$.next(false);
    }),
  );

  activeSectionRoute$ = this.pageContainerService.activeSectionRoute;

  ngAfterViewInit() {
    this.checkStaticButtons(this.router.url);
  }

  selectSection(item: NavigationItem) {
    if (item.children.length) {
      this.onSelectSection.emit(item);
    } else {
      this.pageContainerService.activeSectionRoute.next(item);
      this.router.navigate([item.routeUrl]);
    }
  }

  openTools() {
    this.onToolsOpen.emit();
  }

  goToHome(url: string) {
    this.pageContainerService.activeSectionRoute.next(null);
    this.router.navigate([url]);
  }

  navigateToProfile() {
    const url = this.profile()?.profileRoute;
    if (url?.length) this.router.navigate([url]);
  }

  navigateToSetting() {
    const url = this.settingURL();
    if (url?.length) this.router.navigate([url]);
  }

  checkStaticButtons(url: string) {
    if (url === this.homeButton().routeUrl) {
      this.isStaticButtonActive.set('home');
    } else if (url === this.profile().profileRoute) {
      this.isStaticButtonActive.set('profile');
    } else if (url === this.settingURL()) {
      this.isStaticButtonActive.set('sitting');
    } else {
      this.isStaticButtonActive.set('');
    }
  }

  public toggleMenu(event: Event, menu: Menu) {
    menu.toggle(event);

    this._renderer.setStyle(
      menu.el.nativeElement,
      'top',
      `${(event as any).y}px !important`,
    );
    this._renderer.setStyle(
      menu.el.nativeElement,
      'left',
      `${(event as any).x}px !important`,
    );
  }

  public signOut() {
    this._sharedService.logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: res => {},
      error: (error: HttpErrorResponse) => {},
      complete: () => {
        this.pageContainerService.activeSectionRoute.next(null);
        this.authenticationService.removeUserData();
        this._router.navigate(['/login']);
      },
    });
  }
}
